import { cn } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { DateCell, UuidCell } from '@/components/data-table/cells';
import { StatusChip } from '@/components/ui/status-chip';
import { formatInstrumentPair, splitInstrument, type TradeRow, type TradeSide } from '@/services/api/trades';
import { shortUuid } from '@/utils/string';

const INSTRUMENT_ICON_SIZE = 22;
/** External assets host, kept from legacy — no fallback exists for unknown codes. */
const CRYPTO_ICON_BASE_URL = 'https://assets.onetrading.com/img/crypto';

const EMPTY_PLACEHOLDER = <span className="text-muted-foreground">—</span>;

interface InstrumentIconProps {
  code: string;
  isOverlapped?: boolean;
}

const InstrumentIcon = ({ code, isOverlapped = false }: InstrumentIconProps) => {
  const [isImageBroken, setIsImageBroken] = useState(false);
  // Broken img for exotic codes in legacy — alt="" + onError hide instead
  if (isImageBroken) return null;
  return (
    <img
      alt=""
      className={cn('shrink-0', isOverlapped && '-ml-2.5')}
      height={INSTRUMENT_ICON_SIZE}
      onError={() => setIsImageBroken(true)}
      src={`${CRYPTO_ICON_BASE_URL}/${code}.svg`}
      width={INSTRUMENT_ICON_SIZE}
    />
  );
};
InstrumentIcon.displayName = 'InstrumentIcon';

/** Two overlapped coin icons (quote pulled 10px left) + 'BTC/USD_P'-style pair text. */
const InstrumentCell = ({ instrument }: { instrument: string }) => {
  const { base, quote } = splitInstrument(instrument);
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="inline-flex items-center">
        <InstrumentIcon code={base} />
        <InstrumentIcon code={quote} isOverlapped />
      </span>
      {formatInstrumentPair(instrument)}
    </span>
  );
};
InstrumentCell.displayName = 'InstrumentCell';

const SideCell = ({ side }: { side: TradeSide }) => <StatusChip variant={side === 'BUY' ? 'success' : 'destructive'}>{side}</StatusChip>;
SideCell.displayName = 'SideCell';

/** Raw number, deliberately no precision formatting (legacy parity). */
const RawNumberCell = ({ value }: { value: number }) => <span className="whitespace-nowrap tabular-nums">{value}</span>;
RawNumberCell.displayName = 'RawNumberCell';

/** Truncated id + full-value tooltip + open-in-new-tab to the user page. */
const AccountHolderCell = ({ value }: { value: string }) => {
  // '' when bop-api's enrichment failed — a dash instead of the legacy '…' garbage
  // (deliberate fix)
  if (!value) return EMPTY_PLACEHOLDER;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs" title={value}>
      <a className="inline-flex items-center gap-1 underline underline-offset-2" href={`/operations/users/${value}`} rel="noreferrer" target="_blank">
        {shortUuid(value)}
        <ExternalLink className="size-3" />
      </a>
    </span>
  );
};
AccountHolderCell.displayName = 'AccountHolderCell';

/**
 * Column order + formatting. Sorting is disabled everywhere for
 * legacy parity — the API paginates by cursor, so sorting would only reorder
 * the loaded rows and mislead.
 */
export const TRADE_TABLE_COLUMNS: ColumnDef<TradeRow, unknown>[] = [
  {
    accessorKey: 'instrument',
    header: 'Market',
    enableSorting: false,
    cell: ({ row }) => <InstrumentCell instrument={row.original.instrument} />,
  },
  {
    accessorKey: 'side',
    header: 'Side',
    enableSorting: false,
    cell: ({ row }) => <SideCell side={row.original.side} />,
  },
  {
    accessorKey: 'price',
    header: 'Price',
    enableSorting: false,
    cell: ({ row }) => <RawNumberCell value={row.original.price} />,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    enableSorting: false,
    cell: ({ row }) => <RawNumberCell value={row.original.amount} />,
  },
  {
    accessorKey: 'accountHolderId',
    header: 'Account Holder ID',
    enableSorting: false,
    cell: ({ row }) => <AccountHolderCell value={row.original.accountHolderId} />,
  },
  {
    accessorKey: 'tradeId',
    header: 'Trade ID',
    enableSorting: false,
    cell: ({ row }) => <UuidCell value={row.original.tradeId} />,
  },
  {
    accessorKey: 'time',
    header: 'Date / Time',
    enableSorting: false,
    cell: ({ row }) => <DateCell value={row.original.time} />,
  },
];
